import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, Zap, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { billing } = useBilling();
  const [catalog, setCatalog] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/billing/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoadingKey(null);
    }
  };

  const currentPlan = billing?.plan || "free";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <div className="text-center">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Plans & Billing</p>
          <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Build without limits</h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Start free. Upgrade for unlimited projects, AI re-estimates and blueprint saves — or grab credits as you go.
          </p>
        </div>

        {billing && (
          <div className="mx-auto mt-6 flex max-w-md items-center justify-center gap-4 border border-border p-3 font-mono2 text-xs uppercase tracking-widest">
            <span className="text-muted-foreground">Current: <span className="text-primary">{billing.plan_name}</span></span>
            <span className="h-3 w-px bg-border" />
            <span className="text-muted-foreground">Credits: <span className="text-foreground">{billing.credits}</span></span>
            {billing.project_limit != null && (
              <>
                <span className="h-3 w-px bg-border" />
                <span className="text-muted-foreground">Projects: <span className="text-foreground">{billing.projects_used}/{billing.project_limit}</span></span>
              </>
            )}
          </div>
        )}

        {/* interval toggle */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={`font-mono2 text-xs uppercase tracking-widest ${!annual ? "text-primary" : "text-muted-foreground"}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-interval-toggle" />
          <span className={`font-mono2 text-xs uppercase tracking-widest ${annual ? "text-primary" : "text-muted-foreground"}`}>
            Annual <span className="text-accent">(2 months free)</span>
          </span>
        </div>

        {!catalog ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {catalog.plans.map((p, i) => {
                const isCurrent = currentPlan === p.id;
                const price = annual ? p.price_yearly : p.price_monthly;
                const lookup = p.id === "free" ? null : annual ? p.yearly_lookup : p.monthly_lookup;
                const highlight = p.id === "vip";
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    className={`flex flex-col border p-6 ${highlight ? "border-primary" : "border-border"}`} data-testid={`plan-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                      {isCurrent ? (
                        <span className="border border-primary px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary">Current</span>
                      ) : highlight ? (
                        <span className="bg-primary px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-black">VIP</span>
                      ) : null}
                    </div>
                    <div className="mt-4 font-mono2">
                      <span className="text-4xl font-bold text-foreground">${price}</span>
                      <span className="text-sm text-muted-foreground">{p.id === "free" ? "" : annual ? " / yr" : " / mo"}</span>
                    </div>
                    <ul className="mt-6 flex-1 space-y-2.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      disabled={isCurrent || p.id === "free" || loadingKey === lookup}
                      onClick={() => lookup && checkout(lookup)}
                      data-testid={`checkout-${p.id}`}
                      className={`mt-6 w-full rounded-sm font-mono2 uppercase tracking-widest ${
                        highlight ? "bg-primary text-black hover:bg-primary/90" : "border border-border bg-transparent hover:bg-secondary"
                      }`}>
                      {loadingKey === lookup ? <Loader2 className="h-4 w-4 animate-spin" /> :
                        isCurrent ? "Current Plan" : p.id === "free" ? "Free Forever" : <><Zap className="mr-1 h-4 w-4" /> Upgrade</>}
                    </Button>
                  </motion.div>
                );
              })}
            </div>

            {/* credit packs */}
            <div className="mt-14">
              <div className="mb-4 flex items-center gap-2">
                <Coins className="h-4 w-4 text-primary" />
                <h2 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">One-time estimate credits</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {catalog.credit_packs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-border p-5" data-testid={`pack-${c.id}`}>
                    <div>
                      <p className="font-heading text-lg font-semibold">{c.name}</p>
                      <p className="font-mono2 text-xs text-muted-foreground">Each credit adds one extra project on the Free plan.</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono2 text-2xl font-bold text-primary">${c.price}</span>
                      <Button onClick={() => checkout(c.lookup)} disabled={loadingKey === c.lookup} data-testid={`checkout-${c.id}`}
                        className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                        {loadingKey === c.lookup ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-8 text-center font-mono2 text-xs text-muted-foreground">
              Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
